/**
 * Wave 8 — Compact EOIP + Enterprise Memory (advisory only).
 */
import React from 'react';
import { Alert, Button, Card, Empty, List, Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { eoipService } from '@/services/eoip.service';
import { enterpriseMemoryService } from '@/services/enterpriseMemory.service';
import {
  filterCommercialEoipCards,
  filterCommercialMemories,
} from './commercialIntelligenceFilter';

const { Text } = Typography;

function priorityColor(p: string): string {
  if (/critical|high/i.test(p)) return 'error';
  if (/medium/i.test(p)) return 'warning';
  return 'default';
}

export type EpcCommercialIntelligenceCompactProps = {
  /** Optional search hint for EM (e.g. opportunity name) */
  memoryQueryHint?: string;
  title?: string;
};

export const EpcCommercialIntelligenceCompact: React.FC<
  EpcCommercialIntelligenceCompactProps
> = ({ memoryQueryHint, title = 'EOIP / Memory (advisory)' }) => {
  const eoipQ = useQuery({
    queryKey: ['eoip-dashboard', 'epc-commercial-compact'],
    queryFn: () => eoipService.getDashboard(),
    staleTime: 60_000,
    retry: false,
  });

  const emQ = useQuery({
    queryKey: ['enterprise-memory', 'commercial', memoryQueryHint || ''],
    queryFn: async () => {
      if (memoryQueryHint?.trim()) {
        try {
          const searched = await enterpriseMemoryService.search(memoryQueryHint.trim());
          if (Array.isArray(searched) && searched.length) {
            return searched as unknown as Awaited<
              ReturnType<typeof enterpriseMemoryService.listMemories>
            >;
          }
        } catch {
          /* fall through to list */
        }
      }
      return enterpriseMemoryService.listMemories({ domain: 'commercial', limit: 8 });
    },
    staleTime: 60_000,
    retry: false,
  });

  const cards = filterCommercialEoipCards(eoipQ.data?.cards, 6);
  const memories = filterCommercialMemories(
    (emQ.data || []) as Parameters<typeof filterCommercialMemories>[0],
    5
  );

  return (
    <Card
      size="small"
      title={title}
      extra={
        <Space wrap>
          <Link to="/owner-intelligence/operational-intelligence">EOIP</Link>
          <Link to="/ai/enterprise-memory">Memory</Link>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Advisory only — never bypasses Approval Runtime or mutates ERP"
      />

      {eoipQ.data?.degrade?.jobs_disabled ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="EOIP jobs disabled — showing last stored advisory state"
        />
      ) : null}

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Signals
      </Text>
      {eoipQ.isError ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="EOIP unavailable"
          description={(eoipQ.error as Error)?.message}
        />
      ) : cards.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No commercial EOIP cards" />
      ) : (
        <List
          size="small"
          dataSource={cards}
          style={{ marginBottom: 16 }}
          renderItem={(c) => (
            <List.Item>
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Space wrap>
                  <Tag color={priorityColor(c.priority)}>{c.priority}</Tag>
                  <Text>{c.headline}</Text>
                </Space>
                {c.why ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {c.why}
                  </Text>
                ) : null}
              </Space>
            </List.Item>
          )}
        />
      )}

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Enterprise Memory
      </Text>
      {emQ.isError ? (
        <Alert
          type="warning"
          showIcon
          message="Memory unavailable"
          description={(emQ.error as Error)?.message}
        />
      ) : memories.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No commercial memories" />
      ) : (
        <List
          size="small"
          dataSource={memories}
          renderItem={(m) => (
            <List.Item
              actions={[
                <Link key="open" to={`/ai/enterprise-memory/${m.id}`}>
                  Open
                </Link>,
              ]}
            >
              <Space direction="vertical" size={0}>
                <Text>{m.title}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {m.summary}
                </Text>
              </Space>
            </List.Item>
          )}
        />
      )}

      <Button
        type="link"
        size="small"
        style={{ padding: 0, marginTop: 8 }}
        onClick={() => {
          void eoipQ.refetch();
          void emQ.refetch();
        }}
      >
        Refresh advisory
      </Button>
    </Card>
  );
};

export default EpcCommercialIntelligenceCompact;
